import { SideDrawerLayout } from 'layouts';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { composerSettings, helpers } from '@ddc-webplatform/cmsc-modules';
import { SitemapApi } from 'api';
import { drawerWidth, canEditNavigation } from 'constants/ui';
import { getLocalePaths, getPathError } from 'helpers/sitemapPage';
import { PageDetailsProps } from 'models';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { fireTrackableEvent } from 'helpers/analytics';
import { TRACKING_EVENT } from 'constants/trackingEvents';
import { getSitemapChangedFields } from 'helpers/sitemapTracking';
import { SaveErrorBanner, PageFormFields, SitemapOptions } from './childs';

/** Build form data object from page data */
const buildFormData = (pd: PageDetailsProps['pageData']) => ({
	pagePath: pd?.pagePath || '',
	localePaths: getLocalePaths(pd),
	pageAlias: pd?.pageAlias || '',
	pageId: pd?.pageId || '',
	showInHtmlSitemap: pd?.showInHtmlSitemap ?? false,
	showInXmlSitemap: pd?.showInXmlSitemap ?? false,
	addToSitemapOn: pd?.addToSitemapOn || new Date().toISOString(),
	removeFromSitemapOn: pd?.removeFromSitemapOn ?? null,
	noIndex: pd?.noIndex ?? false,
	noFollow: pd?.noFollow ?? false
});

const PageDetails = ({
	open,
	onClose,
	onSave,
	onHasChangesChange,
	pageData,
	pageIdOptions = [],
	isLoading = false,
	loadError = false
}: PageDetailsProps) => {
	const intl = useIntl();
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [pathError, setPathError] = useState<string | null>(null);
	const [localePathErrors, setLocalePathErrors] = useState<
		Record<string, string | null>
	>({});
	const [aliasError, setAliasError] = useState<string | null>(null);
	const [startDateError, setStartDateError] = useState<string | null>(null);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [formData, setFormData] = useState(() => buildFormData(pageData));
	const [initialFormData, setInitialFormData] = useState(formData);

	// Reset form when pageData changes or when the drawer reopens (handles external-discard
	// where pageData reference hasn't changed but dirty state should be cleared)
	useEffect(() => {
		if (!open || !pageData) return;
		const data = buildFormData(pageData);
		setFormData(data);
		setInitialFormData(data);
		setStartDateError(null);
		setPathError(null);
		setLocalePathErrors({});
		setAliasError(null);
		setSaveError(null);
	}, [open, pageData]);

	// Determine whether the current page is owned by this site.
	// Mirrors the legacy getPageOwnershipData / isSiteOwnedPage check in AddPageWindow.
	const isSiteOwnedPage = useMemo(() => {
		if (!formData.pageId) return false;
		const { siteId } = composerSettings.getComposerSettings();
		if (!siteId) return true; // can't verify – default to enabled
		return helpers.getPageOwnershipData(siteId, formData.pageId).isSiteOwnedPage;
	}, [formData.pageId]);

	const endDateError = useMemo((): string | null => {
		if (!formData.showInXmlSitemap) return null;
		if (!formData.removeFromSitemapOn || !formData.addToSitemapOn) return null;
		if (
			!dayjs(formData.removeFromSitemapOn).isAfter(
				dayjs(formData.addToSitemapOn)
			)
		) {
			return intl.formatMessage({
				id: 'SITEMAP_END_DATE_INVALID',
				defaultMessage: 'End date must be after the start date'
			});
		}
		return null;
	}, [
		formData.showInXmlSitemap,
		formData.removeFromSitemapOn,
		formData.addToSitemapOn,
		intl
	]);

	/** Compare current form data with initial values to determine if save should be enabled */
	const hasChanges = useMemo(() => {
		if (formData.pageId !== initialFormData.pageId) return true;
		if (formData.showInHtmlSitemap !== initialFormData.showInHtmlSitemap)
			return true;
		if (formData.showInXmlSitemap !== initialFormData.showInXmlSitemap)
			return true;
		const xmlWasActive = formData.showInXmlSitemap || initialFormData.showInXmlSitemap;
		if (xmlWasActive && formData.addToSitemapOn !== initialFormData.addToSitemapOn) return true;
		if (xmlWasActive && formData.removeFromSitemapOn !== initialFormData.removeFromSitemapOn)
			return true;
		if (formData.noIndex !== initialFormData.noIndex) return true;
		if (formData.noFollow !== initialFormData.noFollow) return true;
		if (formData.localePaths.length !== initialFormData.localePaths.length)
			return true;
		for (let i = 0; i < formData.localePaths.length; i++) {
			if (formData.localePaths[i].path !== initialFormData.localePaths[i]?.path)
				return true;
		}
		return false;
	}, [formData, initialFormData]);

	// Notify parent whenever unsaved-changes status changes
	useEffect(() => {
		onHasChangesChange?.(hasChanges);
	}, [hasChanges, onHasChangesChange]);

	const handleCheckboxChange = useCallback((field: string, value: boolean) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		if (field === 'showInXmlSitemap') {
			setStartDateError(null);
		}
	}, []);

	const handlePathChange = useCallback(
		(idx: number, value: string) => {
			const isFirst = idx === 0;
			setFormData(prev => ({
				...prev,
				...(isFirst ? { pagePath: value } : {}),
				localePaths: prev.localePaths.map((lp, i) =>
					i === idx ? { locale: lp.locale, path: value } : lp
				)
			}));
			const err = getPathError(value, intl.formatMessage);
			setLocalePathErrors(prev => ({
				...prev,
				[formData.localePaths[idx]?.locale]: err
			}));
			if (isFirst) {
				setPathError(err);
			}
		},
		[intl, formData.localePaths]
	);

	const handlePageIdChange = useCallback((value: string) => {
		setFormData(prev => ({ ...prev, pageId: value }));
	}, []);

	const handleStartDateChange = useCallback((value: string) => {
		setFormData(prev => ({ ...prev, addToSitemapOn: value }));
	}, []);

	const handleEndDateChange = useCallback((value: string) => {
		setFormData(prev => ({ ...prev, removeFromSitemapOn: value }));
	}, []);

	const handleSave = useCallback(async () => {
		if (canEditNavigation && !formData.pageId) return;

		setIsSaving(true);
		setSaveError(null);
		setPathError(null);
		setLocalePathErrors({});
		setAliasError(null);
		setStartDateError(null);

		// Validate required start date when XML sitemap is enabled
		if (formData.showInXmlSitemap && !formData.addToSitemapOn) {
			setStartDateError(
				intl.formatMessage({
					id: 'SITEMAP_START_DATE_REQUIRED',
					defaultMessage: 'Start date is required.'
				})
			);
			setIsSaving(false);
			return;
		}

		try {
			// Only validate locale paths that differ from initial values
			const changedPaths = formData.localePaths.filter(
				(lp, idx) => lp.path !== initialFormData.localePaths[idx]?.path
			);

			// Client-side format validation – only for changed paths
			const localeErrors: Record<string, string | null> = {};
			let hasLocaleError = false;
			changedPaths.forEach(lp => {
				const err = getPathError(lp.path, intl.formatMessage);
				if (err) {
					localeErrors[lp.locale] = err;
					hasLocaleError = true;
				}
			});
			if (hasLocaleError) {
				setLocalePathErrors(prev => ({ ...prev, ...localeErrors }));
				const firstErrorLocale = changedPaths.find(
					lp => localeErrors[lp.locale]
				)?.locale;
				if (firstErrorLocale) setPathError(localeErrors[firstErrorLocale]);
				setIsSaving(false);
				return;
			}

			if (formData.localePaths.length > 1) {
				// Skip API validation for paths that already belong to this page's own locales
				// (the /validateMultiple endpoint rejects paths that exist on the site,
				// but re-using the page's own path is valid — see DE741354)
				const ownPaths = new Set(initialFormData.localePaths.map(lp => lp.path));
				const pathsNeedingValidation = changedPaths.filter(
					lp => !ownPaths.has(lp.path)
				);
				if (pathsNeedingValidation.length > 0) {
					const validityMap = await SitemapApi.validateMultiplePagePaths(
						pathsNeedingValidation
					);
					// Build per-locale results for validated paths (null = valid, string = error)
					const apiLocaleErrors: Record<string, string | null> = {};
					let hasInvalidPath = false;
					pathsNeedingValidation.forEach(lp => {
						if (validityMap[lp.locale]) {
							// Explicitly null-out valid locales so stale errors are cleared
							apiLocaleErrors[lp.locale] = null;
						} else {
							apiLocaleErrors[lp.locale] = intl.formatMessage({
								id: 'PAGE_PATH_INVALID'
							});
							hasInvalidPath = true;
						}
					});
					// Always update localePathErrors so valid locales get cleared too
					setLocalePathErrors(prev => ({ ...prev, ...apiLocaleErrors }));
					if (hasInvalidPath) {
						// Only set pathError if the first locale itself is invalid
						const firstLocale = formData.localePaths[0]?.locale;
						if (firstLocale && apiLocaleErrors[firstLocale]) {
							setPathError(apiLocaleErrors[firstLocale]);
						}
						setIsSaving(false);
						return;
					}
				}
			} else if (changedPaths.length > 0) {
				// Single locale – only validate if it was changed
				const pagePath = formData.localePaths[0]?.path || formData.pagePath;
				const pathResult = await SitemapApi.validateSinglePagePath(pagePath);
				if (!pathResult) {
					setPathError(intl.formatMessage({ id: 'PAGE_PATH_INVALID' }));
					setIsSaving(false);
					return;
				}
			}

			await SitemapApi.updateSitemapEntry(formData);
			const updatedFields = getSitemapChangedFields(formData, initialFormData);
			fireTrackableEvent(TRACKING_EVENT.SAVE_PAGE_DETAILS, {
				eventResult: `${updatedFields.join(', ')} Saved`
			});
			onClose();
			onSave?.(formData.localePaths[0]?.path || formData.pagePath);
		} catch (err: unknown) {
			const message =
				err instanceof Error
					? err.message
					: intl.formatMessage({ id: 'ERROR' });
			setSaveError(message);
		} finally {
			setIsSaving(false);
		}
	}, [formData, onClose, onSave, intl, initialFormData]);

	const handleClose = useCallback(() => {
		if (hasChanges) {
			setIsDiscardDialogOpen(true);
		} else {
			onClose();
		}
	}, [hasChanges, onClose]);

	const handleCancel = useCallback(() => {
		fireTrackableEvent(TRACKING_EVENT.CANCEL_PAGE_DETAILS);
		handleClose();
	}, [handleClose]);

	const handleModalCancel = useCallback(() => {
		setIsDiscardDialogOpen(false);
		onClose();
	}, [onClose]);

	const handleModalConfirm = useCallback(() => {
		setIsDiscardDialogOpen(false);
	}, []);

	return (
		<SideDrawerLayout
			open={open}
			onClose={handleClose}
			title={intl.formatMessage({ id: 'PAGE_DETAILS' })}
			subtitle={(pageData?.pagePath || '').replace(/^\//, '')}
			width={drawerWidth}
			offsetLeft={drawerWidth}
			headerProps={{
				slotProps: {
					root: { sx: { bgcolor: '#2C2C2C' } },
					closeButton: {
						sx: { display: 'none' }
					}
				}
			}}
			contentProps={{
				sx: { padding: '8px 8px 8px 12px', bgcolor: '#2C2C2C' }
			}}
			sx={{ zIndex: (t: any) => t.zIndex.drawer - 2 }}
			paperSx={{
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					bottom: 0,
					width: '12px',
					background: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)',
					zIndex: 1,
					pointerEvents: 'none'
				}
			}}
			footer={
				<Box>
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							justifyContent: 'flex-end',
							alignItems: 'center',
							px: 1,
							padding: '4px 8px',
							borderTop: (t: any) => `1px solid ${t.palette.grey[600]}`
						}}
					>
						<Button
							variant="outlined"
							onClick={handleCancel}
							disabled={isSaving}
							sx={{ padding: '5px 15px', height: 40 }}
						>
							<FormattedMessage id="CANCEL" />
						</Button>
						<Button
							variant="contained"
							onClick={handleSave}
							sx={{ padding: '5px 15px', height: 40 }}
							disabled={
								isSaving ||
								(canEditNavigation && !formData.pageId) ||
								!hasChanges ||
								!!pathError ||
								!!aliasError ||
								!!startDateError ||
								!!endDateError ||
								Object.values(localePathErrors).some(Boolean)
							}
							startIcon={
								isSaving ? <CircularProgress size={14} color="inherit" /> : null
							}
						>
							<FormattedMessage id="SAVE" />
						</Button>
					</Box>
				</Box>
			}
		>
			<ConfirmDialog
				open={isDiscardDialogOpen}
				onConfirm={handleModalConfirm}
				onCancel={handleModalCancel}
				onBackdropClose={handleModalConfirm}
				title={<FormattedMessage id="UNSAVED_CHANGES" />}
				content={<FormattedMessage id="UNSAVED_CHANGES_MESSAGE" />}
				confirmLabel={<FormattedMessage id="STAY" />}
				cancelLabel={<FormattedMessage id="DISCARD_CHANGES" />}
			/>
			{isLoading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<CircularProgress size={32} color="inherit" />
				</Box>
			)}
			{!isLoading && loadError && (
				<Box sx={{ p: 2 }}>
					<Typography color="error">
						<FormattedMessage id="ERROR_MESSAGE_PAGE_DETAIL" />
					</Typography>
				</Box>
			)}
			{!isLoading && !loadError && pageData && (
				<Stack spacing={0}>
					<SaveErrorBanner error={saveError} />

					<PageFormFields
						localePaths={formData.localePaths}
						pathError={pathError}
						localePathErrors={localePathErrors}
						onPathChange={handlePathChange}
						pageAlias={formData.pageAlias}
						aliasError={aliasError}
						pageId={formData.pageId}
						pageIdOptions={pageIdOptions}
						onPageIdChange={handlePageIdChange}
						canEditNavigation={canEditNavigation}
					/>

					<SitemapOptions
						showInHtmlSitemap={formData.showInHtmlSitemap}
						showInXmlSitemap={formData.showInXmlSitemap}
						addToSitemapOn={formData.addToSitemapOn}
						removeFromSitemapOn={formData.removeFromSitemapOn}
						startDateError={startDateError}
						endDateError={endDateError}
						noIndex={formData.noIndex}
						noFollow={formData.noFollow}
						isSiteOwnedPage={isSiteOwnedPage}
						onCheckboxChange={handleCheckboxChange}
						onStartDateChange={handleStartDateChange}
						onEndDateChange={handleEndDateChange}
						onStartDateErrorChange={setStartDateError}
					/>
				</Stack>
			)}
		</SideDrawerLayout>
	);
};

export default memo(PageDetails);
